import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import api from '../../utils/api';
import { connectSocket } from '../../utils/socket';
import toast from 'react-hot-toast';
import { Select, Textarea, Button, Badge, LoadingState, EmptyState } from '../../components/ui';

const MESSAGE_TEMPLATES = [
  { label: 'Return to exam', text: 'Please return to the exam window immediately.' },
  { label: 'Tab switch warning', text: 'Tab switching has been detected. This is a formal warning.' },
  { label: 'Final warning', text: 'Final warning: The next violation will result in exam termination.' },
  { label: 'Stay in fullscreen', text: 'Please ensure you are in fullscreen mode at all times.' },
  { label: 'No external help', text: 'Do not use any external resources or assistance during the exam.' }
];

const MessagingPanel = () => {
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('ALL');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const res = await api.get('/proctor/exams');
        const list = res.data.data?.exams || [];
        setExams(list);
        if (list.length > 0) setSelectedExamId(list[0]._id || list[0].id);
      } catch (err) {
        toast.error('Failed to load exams');
      }
    };
    fetchExams();
  }, []);

  useEffect(() => {
    if (!selectedExamId) return;

    const fetchData = async () => {
      setLoadingMessages(true);
      try {
        const studRes = await api.get(`/exams/${selectedExamId}/students`);
        const raw = studRes.data.data || [];
        setStudents(raw.map(item => item.student || item));
        setMessages([]);
      } catch (err) {
        setStudents([]);
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchData();

    const socket = connectSocket();
    socketRef.current = socket;
    socket.emit('join-proctor', { examId: selectedExamId });

    socket.on('message-sent', (msg) => {
      setMessages(prev => [...prev, msg]);
    });

    return () => {
      socket.off('message-sent');
    };
  }, [selectedExamId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!selectedExamId) {
      toast.error('Please select an exam');
      return;
    }
    setSending(true);
    try {
      const payload = {
        examId: selectedExamId,
        content: messageText.trim(),
        recipientType: selectedRecipient === 'ALL' ? 'ALL' : 'INDIVIDUAL',
        studentId: selectedRecipient !== 'ALL' ? selectedRecipient : undefined
      };
      await api.post('/proctor/message', payload);

      if (socketRef.current) {
        if (selectedRecipient === 'ALL') {
          socketRef.current.emit('send-message', { examId: selectedExamId, content: messageText.trim() });
        } else {
          socketRef.current.emit('send-message', { examId: selectedExamId, recipientId: selectedRecipient, content: messageText.trim() });
        }
      }

      const newMsg = {
        _id: Date.now(),
        content: messageText.trim(),
        recipientType: selectedRecipient === 'ALL' ? 'ALL' : 'INDIVIDUAL',
        recipientName: selectedRecipient === 'ALL' ? 'All Students' : students.find(s => (s._id || s.id) === selectedRecipient)?.name || 'Student',
        sentAt: new Date().toISOString(),
        sender: 'Me'
      };
      setMessages(prev => [...prev, newMsg]);
      setMessageText('');
      toast.success('Message sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSend();
    }
  };

  return (
    <div className="flex h-full" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Left: Compose Panel */}
      <div className="w-96 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Send Message</h2>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <Select label="Exam" value={selectedExamId} onChange={e => setSelectedExamId(e.target.value)}>
            <option value="">Select exam...</option>
            {exams.map(e => <option key={e._id || e.id} value={e._id || e.id}>{e.name}</option>)}
          </Select>

          <Select label="Send To" value={selectedRecipient} onChange={e => setSelectedRecipient(e.target.value)}>
            <option value="ALL">All Students ({students.length})</option>
            {students.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name} ({s.username})</option>)}
          </Select>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quick Templates</label>
            <div className="space-y-1.5">
              {MESSAGE_TEMPLATES.map((tmpl, i) => (
                <button
                  key={i}
                  onClick={() => setMessageText(tmpl.text)}
                  className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors ${messageText === tmpl.text ? 'border-themePrimary bg-themePrimary/5 text-themePrimary' : 'border-slate-200 hover:border-themePrimary/40 text-slate-600 hover:bg-slate-50'}`}
                >
                  <span className="font-medium text-slate-700">{tmpl.label}:</span>{' '}
                  <span className="text-slate-500">{tmpl.text}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Textarea
              label={<>Message <span className="text-slate-400 font-normal">(Ctrl+Enter to send)</span></>}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={4}
              placeholder="Type your message..."
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{messageText.length} chars</p>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200">
          <Button
            variant="primary"
            fullWidth
            size="lg"
            icon={Send}
            loading={sending}
            disabled={!messageText.trim() || !selectedExamId}
            onClick={handleSend}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </div>
      </div>

      {/* Right: Message History */}
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="p-4 bg-white border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-800">Message History</h3>
          <p className="text-xs text-slate-500 mt-0.5">{messages.length} message{messages.length !== 1 ? 's' : ''} sent</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loadingMessages ? (
            <LoadingState label="Loading..." />
          ) : messages.length === 0 ? (
            <EmptyState icon={MessageSquare} title="No messages sent yet" />
          ) : (
            messages.map((msg, idx) => (
              <div key={msg._id || idx} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <Badge variant={msg.recipientType === 'ALL' || msg.recipient === 'ALL' ? 'brand' : 'info'}>
                    {msg.recipientType === 'ALL' || msg.recipient === 'ALL' ? 'All Students' : msg.recipientName || 'Individual'}
                  </Badge>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString() : 'Just now'}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{msg.content || msg.message}</p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default MessagingPanel;
